/**
 * The range a client's network address covers, first to last (ADR 0174),
 * shown beside it as typed: `203.0.113.39/29 · covers .32 to .39`. The owner
 * typed `.39/29` for a block that was .40 to .47 (found on staging,
 * 2026-10-19): a /29 is aligned to a multiple of 8, so .39/29 is .32 to .39,
 * and nothing showed it. Worked out here from the address the API gives, so
 * privacy mode's made-up range (ADR 0160) covers its own made-up addresses.
 */

interface Parsed {
  version: 4 | 6;
  value: bigint;
  bits: number;
}

const OCTET = /^(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;
const HEXTET = /^[0-9a-f]{1,4}$/i;

function ipv4(text: string): bigint | null {
  const octets = text.split(".");
  if (octets.length !== 4 || !octets.every((o) => OCTET.test(o))) {
    return null;
  }
  return octets.reduce((value, o) => (value << 8n) | BigInt(o), 0n);
}

function hextets(part: string): string[] {
  return part === "" ? [] : part.split(":");
}

function ipv6(text: string): bigint | null {
  let groups = text;
  let tail: bigint | null = null;
  const lastColon = groups.lastIndexOf(":");
  if (groups.includes(".")) {
    // An IPv4 address in its last 32 bits ("::ffff:203.0.113.10").
    tail = ipv4(groups.slice(lastColon + 1));
    if (tail === null) {
      return null;
    }
    groups = `${groups.slice(0, lastColon + 1)}0:0`;
  }
  const halves = groups.split("::");
  if (halves.length > 2) {
    return null;
  }
  const head = hextets(halves[0] ?? "");
  const rest = halves.length === 2 ? hextets(halves[1] ?? "") : [];
  const missing = 8 - head.length - rest.length;
  if (halves.length === 2 ? missing < 1 : missing !== 0) {
    return null;
  }
  const all = [...head, ...Array<string>(halves.length === 2 ? missing : 0).fill("0"), ...rest];
  if (!all.every((h) => HEXTET.test(h))) {
    return null;
  }
  const value = all.reduce((v, h) => (v << 16n) | BigInt(`0x${h}`), 0n);
  return tail === null ? value : (value & ~0xffffffffn) | tail;
}

function parsed(text: string): Parsed | null {
  const [address = "", prefix, ...more] = text.trim().split("/");
  if (more.length > 0) {
    return null;
  }
  const version = address.includes(":") ? 6 : 4;
  const value = version === 4 ? ipv4(address) : ipv6(address);
  const width = version === 4 ? 32 : 128;
  const bits = prefix === undefined ? width : /^\d{1,3}$/.test(prefix) ? Number(prefix) : NaN;
  if (value === null || !(bits >= 0 && bits <= width)) {
    return null;
  }
  return { version, value, bits };
}

function written(version: 4 | 6, value: bigint): string {
  if (version === 4) {
    return [24n, 16n, 8n, 0n].map((shift) => String((value >> shift) & 0xffn)).join(".");
  }
  const groups = [112n, 96n, 80n, 64n, 48n, 32n, 16n, 0n].map((shift) =>
    ((value >> shift) & 0xffffn).toString(16),
  );
  // RFC 5952: the longest run of two or more zero groups, the first if tied, is "::".
  let best = { start: -1, length: 1 };
  for (let start = 0; start < 8; ) {
    let end = start;
    while (end < 8 && groups[end] === "0") {
      end += 1;
    }
    if (end - start > best.length) {
      best = { start, length: end - start };
    }
    start = end === start ? start + 1 : end;
  }
  if (best.start < 0) {
    return groups.join(":");
  }
  const before = groups.slice(0, best.start).join(":");
  const after = groups.slice(best.start + best.length).join(":");
  return `${before}::${after}`;
}

/**
 * The first and last address of a range as the API writes one
 * ("203.0.113.32/29", "2001:db8::/64"). Null for one address alone, or what
 * isn't a range.
 */
export function rangeOf(range: string): { first: string; last: string } | null {
  const found = parsed(range);
  const width = found?.version === 4 ? 32 : 128;
  if (found === null || found.bits === width) {
    return null;
  }
  const hosts = (1n << BigInt(width - found.bits)) - 1n;
  const first = found.value & ~hosts;
  return { first: written(found.version, first), last: written(found.version, first | hosts) };
}

/**
 * IPv4 addresses shortened to their last number (".32") when they share the
 * rest, and `typed` shows it: "203.0.113.39/29 · covers .32 to .39".
 */
function shortened(addresses: string[], typed: string): string[] {
  const first = addresses[0] ?? "";
  const net = first.slice(0, first.lastIndexOf(".") + 1);
  const shared =
    net !== "" && typed.startsWith(net) && addresses.every((a) => !a.includes(":") && a.startsWith(net));
  return shared ? addresses.map((a) => a.slice(net.length - 1)) : addresses;
}

/**
 * What a network address covers, beside it as typed: "covers .32 to .39" for
 * a range, "covers .10 alone" for one address typed with a wider block (kept
 * as the address alone, ADR 0171). Null for one address typed as itself.
 */
export function covers(address: string, typed: string | null): string | null {
  const shown = typed ?? address;
  const range = rangeOf(address);
  if (range !== null) {
    const [first, last] = shortened([range.first, range.last], shown);
    return `covers ${first ?? range.first} to ${last ?? range.last}`;
  }
  const alone = parsed(address);
  if (alone === null || typed === null || typed === address) {
    return null;
  }
  const [only] = shortened([written(alone.version, alone.value)], shown);
  return `covers ${only ?? address} alone`;
}
